import './index.less';
import React, { useEffect, useRef, useState } from 'react';
import { BaseOperateView, BaseStagePlugin, useActiveStageIndex, useFramework, useLayerTransform } from '@/framework';
import { IExportPlugin, PluginType } from '@/version-types';

const ShineItem: React.FC<{ layerId: string }> = React.memo(({ layerId }) => {
    const framework = useFramework();
    const refNode = useLayerTransform(layerId);
    const layer = framework.getLayer(layerId);

    if (!layer) return null;

    return (
        <BaseStagePlugin originIndexOrLayerId={layerId} name="layer-shine">
            <BaseOperateView ref={refNode} layer={layer}>
                <div className="c-layer-shine"></div>
            </BaseOperateView>
        </BaseStagePlugin>
    );
});

const LayerShineView = React.memo(() => {
    const framework = useFramework();
    const shineListRef = useRef<Set<string>>(new Set());
    const [_, refresh] = useState(0);

    useEffect(() => {
        const handlerShow = framework.events.onLayerShine((layerId, duration) => {
            if (!shineListRef.current.has(layerId)) {
                shineListRef.current.add(layerId);
                refresh((v) => v + 1);
                setTimeout(() => {
                    shineListRef.current.delete(layerId);
                    refresh((v) => v + 1);
                }, duration);
            }
        });

        const handleHide = framework.events.onLayerShineHide((layerId) => {
            if (shineListRef.current.has(layerId)) {
                shineListRef.current.delete(layerId);
                refresh((v) => v + 1);
            }
        });

        return () => {
            framework.events.off(handlerShow);
            framework.events.off(handleHide);
        };
    }, []);

    const activeStageIndex = useActiveStageIndex();
    const list = Array.from(shineListRef.current).filter((layerId) => {
        const isLongImageMode = framework.stage.isLongImageMode();
        if (isLongImageMode) {
            return true;
        }
        const originIndex = framework.tryGetLayerOriginIndex(layerId);
        return originIndex === framework.convertStageToOrigin(activeStageIndex);
    });

    return (
        <>
            {list.map((layerId) => {
                return <ShineItem key={layerId} layerId={layerId} />;
            })}
        </>
    );
});

const exportPlugin: IExportPlugin = {
    type: PluginType.STAGE,
    sort: 20,
    name: 'layer-shine',
    render() {
        return <LayerShineView />;
    },
};

export default exportPlugin;
